import { convictionFraction } from '@morfi/backend-sdk-v2/client';
import type { LivelinePoint } from '@morfi/chart';
import { D9_SCALE } from '@/config/solana';
import { windowSecondsForTimeframe } from '@/lib/chart/chart-factory';
import type { ChartInterval } from '@/lib/chart/types';

export function initialMarketShare(totalOutcomeCount: number): number | undefined {
  if (totalOutcomeCount === 0) return undefined;
  // Match TradingStarted's integer PPB division, including its rounding.
  const initialPpb = BigInt(D9_SCALE) / BigInt(totalOutcomeCount);
  return convictionFraction(initialPpb.toString());
}

export function resolveChartRequestStart(args: {
  readonly interval: ChartInterval;
  readonly marketCreatedAt: number;
  readonly anchorMs: number;
  readonly lifespanSecs: number;
}): string {
  // The visible market starts trading after proposal creation. Passing that
  // same start prevents All from choosing buckets for the older proposal.
  if (args.interval === 'all') return new Date(args.marketCreatedAt * 1000).toISOString();
  const requestedWindow =
    args.interval === 'live'
      ? 60_000
      : windowSecondsForTimeframe(args.interval, args.lifespanSecs) * 1000;
  return new Date(
    Math.max(args.marketCreatedAt * 1000, args.anchorMs - requestedWindow),
  ).toISOString();
}

export function extendChartToLeftEdge(args: {
  readonly points: ReadonlyArray<LivelinePoint>;
  readonly leftEdgeValue: number | undefined;
  readonly leftEdgeTime: number;
  /** Exact launch state, distinct from a predecessor for an arbitrary window. */
  readonly initialPoint?: LivelinePoint;
}): LivelinePoint[] {
  const points = [...args.points];
  if (args.initialPoint !== undefined) {
    const initialPoint = args.initialPoint;
    // A bucket labelled before launch represents later activity, not a
    // pre-launch vertex. Keep it out of the curve approaching the launch.
    return [initialPoint, ...points.filter((point) => point.time > initialPoint.time)];
  }
  if (args.leftEdgeValue === undefined) return points;
  if (points.some((point) => point.time === args.leftEdgeTime)) return points;
  // Bucket timestamps can precede the viewport even when their closing value
  // is newer than the API's baseline. Keep that real sample at the edge.
  const value =
    points.findLast((point) => point.time < args.leftEdgeTime)?.value ?? args.leftEdgeValue;
  return [...points, { time: args.leftEdgeTime, value }].toSorted(
    (left, right) => left.time - right.time,
  );
}
